import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  CircularProgress,
  Typography,
  Stack,
  Paper,
  Grid,
  Tooltip,
} from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { activateCarePlan, getCarePlans } from "../../../../../api/services/carePathWay";
import { QK } from "../../../../../base/qk";
import CustomCheckbox from "../../../../CustomForm/CustomCheckbox";
import ConfirmModal from "../../../../ConfirmModal/ConfirmModal";
import { useParams } from "react-router-dom";
import useClientDetails from "../../../../../hooks/useClientDetails";
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoryIcon from '@mui/icons-material/History';

const InactiveCareTabs = ({ refetchActiveCarePlans }) => {
  const [showData, setShowData] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const { id: clientID } = useParams();
  const { fullName } = useClientDetails(clientID);
  const queryClient = useQueryClient();

  // Fetch Inactive Plans
  const { data: inactiveCarePlans = [], isLoading, refetch } = useQuery(
    [QK.inactiveCarePlans, { status: "active", client: clientID }],
    () => getCarePlans({ status: "active", client: clientID })
  );

  // Mutation to activate
  const { mutate: activate, isLoading: activating } = useMutation(
    () => activateCarePlan({ client: clientID, form: selectedPlan.id }),
    {
      onSuccess: () => {
        setShowConfirm(false);
        setSelectedPlan(null);
        refetch();
        refetchActiveCarePlans?.();
        queryClient.invalidateQueries([QK.activeCarePlans, { client: clientID }]);
        queryClient.invalidateQueries([QK.inactiveCarePlans]);
      },
    }
  );

  const handleShowClick = () => setShowData(!showData);

  const handleCheckboxClick = (checked, plan) => {
    if (checked) {
      setSelectedPlan(plan);
      setShowConfirm(true);
    }
  };

  const filteredPlans = inactiveCarePlans.filter((plan) =>
    plan.title?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box sx={{ width: "100%", mb: 3 }}>
      {/* Header Toggle Button */}
      <Button
        fullWidth
        variant="contained"
        sx={{
          justifyContent: "space-between",
          py: 2,
          px: 3,
          mb: 2,
          borderRadius: "12px",
          textTransform: "none",
          fontSize: "1rem",
          fontWeight: 700,
          background: showData
            ? "linear-gradient(135deg, #1e293b, #0f172a)"
            : "linear-gradient(135deg, #3b82f6, #2563eb)",
          color: "white",
          boxShadow: "0 4px 12px rgba(37, 99, 235, 0.2)",
          "&:hover": { opacity: 0.9 },
        }}
        onClick={handleShowClick}
      >
        <span>Inactive Care Plans for {fullName}</span>
        <Typography variant="caption" sx={{ bgcolor: 'rgba(255,255,255,0.2)', px: 1.5, py: 0.5, borderRadius: '20px' }}>
          {filteredPlans.length} Total
        </Typography>
      </Button>

      {showData && (
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            borderRadius: "16px", 
            bgcolor: "#f8fafc", 
            border: "1px solid #e2e8f0",
          }}
        >
          {/* Action Bar */}
          <Stack direction="row" spacing={2} mb={3}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search by plan name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: "text.disabled", mr: 1, fontSize: 18 }} />,
              }}
              sx={{ bgcolor: "white", borderRadius: "8px" }}
            />
            <Tooltip title="Refresh">
              <Button
                variant="outlined"
                onClick={() => { setSearch(""); refetch(); }}
                sx={{ borderRadius: "8px", minWidth: "48px", borderColor: '#e2e8f0' }}
              >
                <RefreshIcon fontSize="small" />
              </Button>
            </Tooltip>
          </Stack>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={30} /></Box>
          ) : filteredPlans.length === 0 ? (
            <Typography align="center" sx={{ py: 4, color: "text.secondary", fontStyle: 'italic' }}>
              No inactive care plans found.
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 500, overflowY: "auto", pr: 1 }}>
              <Grid container spacing={2}>
                {filteredPlans.map((plan) => (
                  <Grid item xs={12} sm={6} md={4} key={plan.id}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        border: "1px solid #e2e8f0",
                        borderRadius: "14px",
                        bgcolor: "white",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          borderColor: "#3b82f6",
                          transform: "translateY(-4px)",
                          boxShadow: "0 12px 20px rgba(0,0,0,0.04)",
                        },
                      }}
                    >
                      {/* Top Content */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <CustomCheckbox
                          name={String(plan.id)}
                          label=""
                          defaultColor="#3b82f6"
                          isInitiallyChecked={false}
                          onClick={(checked) => handleCheckboxClick(checked, plan)}
                        />
                        <Box sx={{ ml: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", mb: 0.5 }}>
                            {plan.title}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              bgcolor: "#fee2e2", 
                              color: "#dc2626", 
                              px: 1, 
                              py: 0.2, 
                              borderRadius: "4px",
                              fontWeight: 700,
                              fontSize: '0.65rem'
                            }}
                          >
                            INACTIVE
                          </Typography>
                        </Box>
                      </Box>
                      
                      {/* Bottom Footer */}
                      <Box sx={{ mt: 'auto', pt: 2, borderTop: '1px dashed #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                         <Stack direction="row" spacing={0.5} alignItems="center">
                            <HistoryIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                               {plan.updated_at ? new Date(plan.updated_at).toLocaleDateString() : "No Date"}
                            </Typography>
                         </Stack>
                         {activating && selectedPlan?.id === plan.id && <CircularProgress size={14} />}
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Paper>
      )}

      {/* Activation Confirmation */}
      <ConfirmModal
        show={showConfirm}
        handleClose={() => {
          setSelectedPlan(null);
          setShowConfirm(false);
          refetch(); // Refetch to reset checkbox state if cancelled
        }}
        handleConfirm={() => activate()}
        title="Move to Active Plans"
        body={`Are you sure you want to activate the plan: "${selectedPlan?.title}"?`}
        isLoading={activating}
        confirmText="Confirm Activation"
        cancelText="Discard"
      />
    </Box>
  );
};

export default InactiveCareTabs;